<template>
  <div class="tab-content invoice-management-page">
    <header class="invoice-hero">
      <div class="hero-copy">
        <span class="hero-eyebrow"><i class="fas fa-file-invoice"></i>{{ $t('controlCenter.invoices.title') }}</span>
        <h1>{{ $t('controlCenter.invoices.title') }}</h1>
        <p class="subtitle">{{ $t('controlCenter.invoices.subtitle') }}</p>
      </div>
      <div class="hero-actions">
        <label class="brand-switcher">
          <span>{{ $t('controlCenter.common.shop') }}</span>
          <el-select v-model="activeBrand" size="small" @change="handleBrandChange">
            <el-option v-for="item in brandOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
          </el-select>
        </label>
        <button class="btn btn-icon-only refresh-btn" :title="$t('controlCenter.common.refresh')" @click="fetchOrders">
          <i class="fas fa-rotate"></i>
        </button>
      </div>
    </header>

    <section class="invoice-metrics" aria-label="Invoice overview">
      <article v-for="metric in invoiceStats" :key="metric.key" class="metric-card" :class="`metric-card--${metric.tone}`">
        <div class="metric-icon"><i :class="metric.icon"></i></div>
        <div class="metric-content">
          <span>{{ metric.label }}</span>
          <strong>{{ metric.value }}</strong>
        </div>
        <span v-if="metric.key === 'coverage'" class="metric-suffix">%</span>
      </article>
    </section>

    <section class="invoice-workspace">
      <div class="workspace-toolbar">
        <div class="workspace-title">
          <div>
            <span class="section-kicker">{{ $t('controlCenter.invoices.columns.invoice') }}</span>
            <h2>{{ $t('controlCenter.orders.title') }}</h2>
          </div>
          <span class="record-count">{{ totalElements }}</span>
        </div>
        <div class="toolbar-actions">
          <button v-if="activeFilterCount" class="text-button" @click="resetFilters">
            <i class="fas fa-eraser"></i>{{ $t('controlCenter.common.reset') }}
          </button>
          <button class="btn btn-primary search-btn" @click="fetchOrders">
            <i class="fas fa-magnifying-glass"></i>{{ $t('controlCenter.common.search') }}
          </button>
        </div>
      </div>

      <div class="filter-panel">
        <el-input
          v-model.trim="filters.keyword"
          :placeholder="$t('controlCenter.orders.keywordPlaceholder')"
          clearable
          class="keyword-input"
          prefix-icon="el-icon-search"
          @keyup.enter.native="fetchOrders"
        ></el-input>
        <el-select v-model="filters.status" clearable class="filter-select" :placeholder="$t('controlCenter.common.status')">
          <el-option :label="$t('controlCenter.common.all')" value=""></el-option>
          <el-option v-for="item in statusOptions" :key="item" :label="statusLabel(item)" :value="item"></el-option>
        </el-select>
        <el-select v-model="filters.planId" clearable class="filter-select" :placeholder="$t('controlCenter.orders.columns.plan')">
          <el-option :label="$t('controlCenter.common.all')" value=""></el-option>
          <el-option v-for="plan in planOptions" :key="plan.value" :label="plan.label" :value="plan.value"></el-option>
        </el-select>
        <el-select v-model="filters.paymentMethod" clearable class="filter-select" :placeholder="$t('controlCenter.orders.columns.paymentMethod')">
          <el-option :label="$t('controlCenter.common.all')" value=""></el-option>
          <el-option v-for="method in paymentMethodOptions" :key="method" :label="method.toUpperCase()" :value="method"></el-option>
        </el-select>
        <span v-if="activeFilterCount" class="filter-count">{{ activeFilterCount }}</span>
      </div>

      <div v-if="activeFilterCount" class="filter-chips">
        <span v-if="filters.keyword" class="filter-chip"><i class="fas fa-search"></i>{{ filters.keyword }}</span>
        <span v-if="filters.status" class="filter-chip">{{ statusLabel(filters.status) }}</span>
        <span v-if="filters.planId" class="filter-chip">{{ planLabel(filters.planId) }}</span>
        <span v-if="filters.paymentMethod" class="filter-chip">{{ filters.paymentMethod.toUpperCase() }}</span>
      </div>

      <div class="invoice-table-wrap">
        <el-table
          class="invoice-table"
          :data="orders"
          v-loading="loading"
          row-key="orderNo"
          :tree-props="{ children: 'children' }"
          default-expand-all
        >
          <el-table-column :label="$t('controlCenter.orders.columns.orderNo')" min-width="190" fixed="left">
            <template slot-scope="scope">
              <div class="order-cell"><span class="order-no">{{ scope.row.orderNo }}</span><span class="order-date">{{ formatDateTime(scope.row.createdAt) }}</span></div>
            </template>
          </el-table-column>
          <el-table-column :label="$t('controlCenter.orders.columns.company')" min-width="190">
            <template slot-scope="scope"><span class="company-name">{{ scope.row.companyName || '-' }}</span></template>
          </el-table-column>
          <el-table-column :label="$t('controlCenter.orders.columns.plan')" min-width="150">
            <template slot-scope="scope">
              <span v-if="scope.row.orderType === 'STORAGE'" class="plan-badge plan-badge--storage"><i class="fas fa-database"></i>{{ $t('controlCenter.orders.storageTag') }}</span>
              <span v-else class="plan-name">{{ planLabel(scope.row.planId) }}</span>
            </template>
          </el-table-column>
          <el-table-column :label="$t('controlCenter.orders.columns.amount')" min-width="145">
            <template slot-scope="scope"><span class="amount-cell">{{ formatAmount(scope.row.finalAmount, scope.row.displayCurrency) }}</span></template>
          </el-table-column>
          <el-table-column :label="$t('controlCenter.common.status')" min-width="125">
            <template slot-scope="scope"><el-tag class="status-pill" size="small" :type="statusTagType(scope.row.status)"><i class="status-dot"></i>{{ statusLabel(scope.row.status) }}</el-tag></template>
          </el-table-column>
          <el-table-column :label="$t('controlCenter.invoices.columns.invoice')" min-width="185">
            <template slot-scope="scope">
              <div class="invoice-status" :class="{ 'is-uploaded': hasUploadedInvoice(scope.row) }">
                <span class="invoice-status-icon"><i :class="hasUploadedInvoice(scope.row) ? 'fas fa-check' : 'fas fa-arrow-up-from-bracket'"></i></span>
                <span class="invoice-status-copy"><strong>{{ hasUploadedInvoice(scope.row) ? $t('controlCenter.invoices.title') : $t('controlCenter.invoices.notUploaded') }}</strong><small>{{ scope.row.invoiceFileName || $t('controlCenter.invoices.notUploaded') }}</small></span>
              </div>
            </template>
          </el-table-column>
          <el-table-column :label="$t('controlCenter.common.actions')" min-width="320" fixed="right">
            <template slot-scope="scope">
              <div class="action-buttons">
                <button class="btn btn-sm action-view" :title="$t('controlCenter.common.viewDetails')" @click="openDetail(scope.row.orderNo)"><i class="fas fa-eye"></i><span>{{ $t('controlCenter.common.viewDetails') }}</span></button>
                <el-upload class="invoice-upload" action="/api/payment/oss/files" :show-file-list="false" :http-request="options => uploadInvoice(scope.row, options)" :before-upload="beforeInvoiceUpload">
                  <button class="btn btn-sm action-upload" :class="{ 'btn-invoice-uploaded': hasUploadedInvoice(scope.row) }" :disabled="uploadingOrderNo === scope.row.orderNo"><i :class="uploadingOrderNo === scope.row.orderNo ? 'fas fa-spinner fa-spin' : 'fas fa-cloud-arrow-up'"></i><span>{{ uploadingOrderNo === scope.row.orderNo ? $t('controlCenter.orders.invoiceUploading') : $t('controlCenter.orders.uploadInvoice') }}</span></button>
                </el-upload>
                <button class="btn btn-sm action-invoice" :disabled="!canViewInvoice(scope.row)" :title="$t('controlCenter.orders.viewInvoice')" @click="viewInvoice(scope.row)"><i class="fas fa-file-lines"></i><span>{{ $t('controlCenter.orders.viewInvoice') }}</span></button>
                <button v-if="hasUploadedInvoice(scope.row)" class="btn btn-sm action-delete" :disabled="deletingOrderNo === scope.row.orderNo" :title="$t('controlCenter.invoices.deleteFile')" @click="deleteInvoice(scope.row)"><i :class="deletingOrderNo === scope.row.orderNo ? 'fas fa-spinner fa-spin' : 'fas fa-trash-can'"></i><span>{{ deletingOrderNo === scope.row.orderNo ? $t('controlCenter.invoices.deleting') : $t('controlCenter.invoices.deleteFile') }}</span></button>
              </div>
            </template>
          </el-table-column>
          <template slot="empty"><div class="table-empty"><i class="fas fa-file-circle-xmark"></i><strong>{{ $t('controlCenter.invoices.notUploaded') }}</strong><span>{{ $t('controlCenter.invoices.subtitle') }}</span></div></template>
        </el-table>
      </div>

      <footer class="workspace-footer">
        <span class="footer-hint"><i class="fas fa-circle-info"></i>{{ $t('controlCenter.invoices.subtitle') }}</span>
        <el-pagination :current-page="pageNumber + 1" :page-size="pageSize" :page-sizes="[10, 20, 50]" :total="totalElements" layout="total, sizes, prev, pager, next" @current-change="handlePageChange" @size-change="handleSizeChange"></el-pagination>
      </footer>
    </section>

    <el-dialog class="invoice-detail-dialog" :visible.sync="detailDialogVisible" width="900px" :show-close="true">
      <div slot="title" class="dialog-title"><span>{{ $t('controlCenter.orders.detailTitle') }}</span><span v-if="selectedOrder" class="dialog-order-no">{{ selectedOrder.orderNo }}</span></div>
      <div v-loading="detailLoading">
        <div v-if="selectedOrder" class="detail-layout">
          <div class="detail-summary">
            <div class="summary-main"><span class="summary-label">{{ $t('controlCenter.orders.columns.company') }}</span><strong>{{ selectedOrder.companyName || '-' }}</strong><span class="summary-meta">{{ planLabel(selectedOrder.planId) }} · {{ billingCycleLabel(selectedOrder.billingCycle) }}</span></div>
            <div class="summary-side"><span class="summary-label">{{ $t('controlCenter.orders.columns.amount') }}</span><strong>{{ formatAmount(selectedOrder.finalAmount, selectedOrder.displayCurrency) }}</strong><el-tag class="status-pill" size="small" :type="statusTagType(selectedOrder.status)"><i class="status-dot"></i>{{ statusLabel(selectedOrder.status) }}</el-tag></div>
          </div>
          <div class="detail-grid detail-grid--compact">
            <div class="detail-item"><span>{{ $t('controlCenter.orders.columns.orderNo') }}</span><strong>{{ selectedOrder.orderNo }}</strong></div>
            <div class="detail-item"><span>{{ $t('controlCenter.orders.columns.createdAt') }}</span><strong>{{ formatDateTime(selectedOrder.createdAt) }}</strong></div>
            <div class="detail-item detail-span-2"><span>{{ $t('controlCenter.invoices.columns.invoice') }}</span><strong class="file-value"><i class="fas fa-paperclip"></i>{{ selectedOrder.invoiceFileName || $t('controlCenter.invoices.notUploaded') }}</strong></div>
          </div>
          <section class="detail-card">
            <div class="detail-card-header"><div><span class="section-kicker">{{ $t('controlCenter.orders.invoiceItems') }}</span><h3>{{ $t('controlCenter.orders.invoiceItems') }}</h3></div></div>
            <div class="detail-card-body"><div v-for="item in selectedOrder.invoiceItems || []" :key="item.matter" class="invoice-row"><span>{{ item.matter }}</span><strong>{{ formatAmount(item.amount, selectedOrder.displayCurrency) }}</strong></div><div v-if="!(selectedOrder.invoiceItems || []).length" class="invoice-info-empty">-</div></div>
          </section>
          <section class="detail-card invoice-info-card">
            <div class="detail-card-header"><div><span class="section-kicker">{{ $t('controlCenter.orders.defaultInvoiceInfo') }}</span><h3>{{ $t('controlCenter.orders.defaultInvoiceInfo') }}</h3></div><span v-if="isSnapshotLocked(selectedOrder)" class="snapshot-badge"><i class="fas fa-lock"></i>{{ $t('controlCenter.invoices.snapshot') }}</span></div>
            <div v-if="defaultInvoiceInfo" class="detail-card-body company-grid">
              <div class="detail-item"><span>{{ $t('controlCenter.accountInfo.invoiceInfo.fields.title') }}</span><strong>{{ defaultInvoiceInfo.title || '-' }}</strong></div>
              <div class="detail-item"><span>{{ $t('controlCenter.accountInfo.invoiceInfo.fields.invoiceType') }}</span><strong>{{ defaultInvoiceInfo.invoiceType || 'VAT Ordinary Invoice' }}</strong></div>
              <div class="detail-item detail-span-2"><span>{{ $t('controlCenter.accountInfo.invoiceInfo.fields.taxId') }}</span><strong>{{ defaultInvoiceInfo.taxId || '-' }}</strong></div>
              <div class="detail-item detail-span-2"><span>{{ $t('controlCenter.accountInfo.invoiceInfo.fields.address') }}</span><strong>{{ defaultInvoiceInfo.address || '-' }}</strong></div>
              <div class="detail-item"><span>{{ $t('controlCenter.accountInfo.invoiceInfo.fields.phone') }}</span><strong>{{ defaultInvoiceInfo.phone || '-' }}</strong></div>
              <div class="detail-item"><span>{{ $t('controlCenter.accountInfo.invoiceInfo.fields.bankName') }}</span><strong>{{ defaultInvoiceInfo.bankName || '-' }}</strong></div>
              <div class="detail-item"><span>{{ $t('controlCenter.accountInfo.invoiceInfo.fields.bankAccount') }}</span><strong>{{ defaultInvoiceInfo.bankAccount || '-' }}</strong></div>
              <div v-if="defaultInvoiceInfo.bankAddresses && defaultInvoiceInfo.bankAddresses.length" class="detail-item detail-span-2"><span>{{ $t('controlCenter.accountInfo.invoiceInfo.fields.bankAddresses') }}</span><strong>{{ defaultInvoiceInfo.bankAddresses.join('; ') }}</strong></div>
            </div>
            <div v-else class="detail-card-body invoice-info-empty"><i class="fas fa-inbox"></i>{{ $t('controlCenter.orders.noDefaultInvoiceInfo') }}</div>
          </section>
          <section class="detail-card">
            <div class="detail-card-header"><div><span class="section-kicker">{{ $t('controlCenter.orders.companyInfo') }}</span><h3>{{ $t('controlCenter.orders.companyInfo') }}</h3></div></div>
            <div class="detail-card-body company-grid">
              <div class="detail-item"><span>{{ $t('controlCenter.orders.contactName') }}</span><strong>{{ selectedOrder.companyContactName || '-' }}</strong></div>
              <div class="detail-item"><span>{{ $t('controlCenter.orders.email') }}</span><strong>{{ selectedOrder.companyEmail || '-' }}</strong></div>
              <div class="detail-item"><span>{{ $t('controlCenter.orders.phone') }}</span><strong>{{ selectedOrder.companyPhone || '-' }}</strong></div>
              <div class="detail-item"><span>{{ $t('controlCenter.orders.registrationNumber') }}</span><strong>{{ selectedOrder.companyRegistrationNumber || '-' }}</strong></div>
              <div class="detail-item detail-span-2"><span>{{ $t('controlCenter.orders.address') }}</span><strong>{{ selectedOrder.companyAddress || '-' }}</strong></div>
            </div>
          </section>
        </div>
      </div>
      <span slot="footer" class="dialog-footer"><el-button @click="detailDialogVisible = false">{{ $t('controlCenter.common.close') }}</el-button></span>
    </el-dialog>
  </div>
</template>

<script>
import { paymentAdminApi } from '@/api/paymentAdmin'
import { accountApi } from '@/api/account'
import brand from '@/branding'
import { getUserInfo } from '@/utils/auth'
import { formatPrice } from '@/utils/currency'

export default {
  name: 'InvoiceManagement',
  data() {
    return {
      loading: false,
      detailLoading: false,
      detailDialogVisible: false,
      orders: [],
      selectedOrder: null,
      defaultInvoiceInfo: null,
      uploadingOrderNo: '',
      deletingOrderNo: '',
      totalElements: 0,
      pageNumber: 0,
      pageSize: 10,
      activeBrand: brand.id,
      brandOptions: [
        { value: 'lpt', label: 'LPT' },
        { value: 'mt', label: 'MindseT' }
      ],
      filters: {
        keyword: '',
        status: '',
        planId: '',
        paymentMethod: ''
      },
      planOptions: [],
      paymentMethodOptions: ['card', 'visa', 'mastercard', 'alipay', 'wechat', 'wechat_pay'],
      statusOptions: ['PENDING', 'SUCCESS', 'ACTIVE', 'FAILED', 'CANCELED', 'PAST_DUE']
    }
  },
  created() {
    this.fetchPurchaseConfig()
    this.fetchOrders()
  },
  computed: {
    activeFilterCount() {
      return Object.values(this.filters).filter(Boolean).length
    },
    invoiceStats() {
      const visibleOrders = this.orders.length
      const uploaded = this.orders.filter(order => this.hasUploadedInvoice(order)).length
      const pending = Math.max(visibleOrders - uploaded, 0)
      const coverage = visibleOrders ? Math.round((uploaded / visibleOrders) * 100) : 0
      return [
        { key: 'orders', label: this.$t('controlCenter.invoices.overview.total'), value: this.totalElements, icon: 'fas fa-receipt', tone: 'neutral' },
        { key: 'uploaded', label: this.$t('controlCenter.invoices.overview.uploaded'), value: uploaded, icon: 'fas fa-file-circle-check', tone: 'success' },
        { key: 'pending', label: this.$t('controlCenter.invoices.overview.pending'), value: pending, icon: 'fas fa-clock', tone: 'warning' },
        { key: 'coverage', label: this.$t('controlCenter.invoices.overview.coverage'), value: coverage, icon: 'fas fa-chart-pie', tone: 'accent' }
      ]
    }
  },
  methods: {
    async fetchPurchaseConfig() {
      try {
        const response = await paymentAdminApi.getPurchaseConfig(this.activeBrand)
        const data = response && response.data ? response.data : response
        this.planOptions = Object.keys(data.plans || {}).map(key => ({
          value: key,
          label: data.plans[key].name
        }))
        this.paymentMethodOptions = Array.from(new Set([
          ...(data.paymentMethods || []),
          ...this.paymentMethodOptions
        ]))
      } catch (error) {
        console.error('Failed to fetch purchase config:', error)
      }
    },
    async fetchOrders() {
      this.loading = true
      try {
        const response = await paymentAdminApi.getOrders({
          brandCode: this.activeBrand,
          keyword: this.filters.keyword || undefined,
          status: this.filters.status || undefined,
          planId: this.filters.planId || undefined,
          paymentMethod: this.filters.paymentMethod || undefined,
          page: this.pageNumber,
          size: this.pageSize
        })
        const data = response && response.data ? response.data : response
        this.orders = data.content || []
        this.totalElements = data.totalElements || 0
        this.pageNumber = data.pageNumber || 0
        this.pageSize = data.pageSize || this.pageSize
      } catch (error) {
        console.error('Failed to fetch orders:', error)
        this.$message.error(this.$t('controlCenter.orders.loadError'))
      } finally {
        this.loading = false
      }
    },
    async openDetail(orderNo) {
      this.detailDialogVisible = true
      this.detailLoading = true
      this.defaultInvoiceInfo = null
      try {
        const response = await paymentAdminApi.getOrder(orderNo, this.activeBrand)
        this.selectedOrder = response && response.data ? response.data : response
        const snapshot = this.selectedOrder.invoiceInfoSnapshot
        if (snapshot && typeof snapshot === 'object' && Object.keys(snapshot).length > 0 && snapshot.title) {
          this.defaultInvoiceInfo = snapshot
        } else {
          const invoiceResponse = await accountApi.getInvoiceInfosForOrder(this.selectedOrder.orderNo || orderNo)
          const invoiceInfos = invoiceResponse && invoiceResponse.data ? invoiceResponse.data : invoiceResponse
          const infos = invoiceInfos || []
          this.defaultInvoiceInfo = infos.find(item => item.defaultInfo) || infos[0] || null
        }
      } catch (error) {
        console.error('Failed to fetch order detail:', error)
        this.$message.error(this.$t('controlCenter.orders.detailError'))
      } finally {
        this.detailLoading = false
      }
    },
    beforeInvoiceUpload(file) {
      const acceptedTypes = [
        'application/pdf',
        'image/png',
        'image/jpeg',
        'image/jpg'
      ]
      const isAccepted = acceptedTypes.includes(file.type)
      const isUnderLimit = file.size / 1024 / 1024 <= 10
      if (!isAccepted) {
        this.$message.error(this.$t('controlCenter.orders.invoiceTypeError'))
        return false
      }
      if (!isUnderLimit) {
        this.$message.error(this.$t('controlCenter.orders.invoiceSizeError'))
        return false
      }
      return true
    },
    async uploadInvoice(row, options) {
      this.uploadingOrderNo = row.orderNo
      try {
        const userInfo = getUserInfo() || {}
        const accountId = userInfo.accountId || userInfo.sub || userInfo.userId
        if (!accountId) {
          throw new Error('Missing accountId')
        }
        const formData = new FormData()
        formData.append('accountId', accountId)
        if (userInfo.userId || userInfo.sub) {
          formData.append('uploaderId', userInfo.userId || userInfo.sub)
        }
        formData.append('files', options.file)
        const uploaded = await paymentAdminApi.uploadInvoiceFile(formData)
        const file = Array.isArray(uploaded) ? uploaded[0] : uploaded
        if (!file || !file.id || !file.url) {
          throw new Error('Invalid upload response')
        }
        const invoicePayload = {
          fileId: file.id,
          fileName: file.filename || options.file.name,
          fileUrl: file.url
        }
        const updateResponse = await paymentAdminApi.updateOrderInvoice(row.orderNo, invoicePayload, this.activeBrand)
        const updatedOrder = this.normalizeUpdatedOrder(updateResponse, row, invoicePayload)
        this.$message.success(this.$t('controlCenter.orders.invoiceUploadSuccess'))
        this.patchOrderInvoice(row.orderNo, updatedOrder)
        if (this.selectedOrder && this.selectedOrder.orderNo === row.orderNo) {
          this.selectedOrder = {
            ...this.selectedOrder,
            ...updatedOrder
          }
        }
        if (options.onSuccess) options.onSuccess(file)
      } catch (error) {
        console.error('Failed to upload invoice:', error)
        this.$message.error(this.$t('controlCenter.orders.invoiceUploadError'))
        if (options.onError) options.onError(error)
      } finally {
        this.uploadingOrderNo = ''
      }
    },
    normalizeUpdatedOrder(response, row, invoicePayload) {
      const order = response && response.data ? response.data : response
      return {
        ...(row || {}),
        ...(order || {}),
        invoiceFileId: (order && order.invoiceFileId) || invoicePayload.fileId,
        invoiceFileName: (order && order.invoiceFileName) || invoicePayload.fileName,
        invoiceFileUrl: (order && order.invoiceFileUrl) || invoicePayload.fileUrl
      }
    },
    patchOrderInvoice(orderNo, updatedOrder) {
      const index = this.orders.findIndex(item => item.orderNo === orderNo)
      if (index === -1 || !updatedOrder) return
      this.$set(this.orders, index, {
        ...this.orders[index],
        invoiceFileId: updatedOrder.invoiceFileId,
        invoiceFileName: updatedOrder.invoiceFileName,
        invoiceFileUrl: updatedOrder.invoiceFileUrl
      })
    },
    canViewInvoice(row) {
      return !!(row && row.orderNo)
    },
    hasUploadedInvoice(row) {
      return !!(row && (row.invoiceFileId || row.invoiceFileUrl || row.invoiceFileName))
    },
    isSnapshotLocked(row) {
      const snapshot = row && row.invoiceInfoSnapshot
      return !!(this.hasUploadedInvoice(row) && snapshot && typeof snapshot === 'object' && Object.keys(snapshot).length)
    },
    viewInvoice(row) {
      this.$router.push({
        path: '/buy/invoice',
        query: { orderNo: row.orderNo }
      })
    },
    async deleteInvoice(row) {
      try {
        await this.$confirm(
          this.$t('controlCenter.invoices.deleteConfirm'),
          this.$t('controlCenter.invoices.deleteFile'),
          {
            confirmButtonText: this.$t('controlCenter.common.confirm'),
            cancelButtonText: this.$t('controlCenter.common.cancel'),
            type: 'warning'
          }
        )
      } catch (error) {
        return
      }

      this.deletingOrderNo = row.orderNo
      try {
        if (!row.invoiceFileId) throw new Error('Invoice file ID is unavailable')
        await paymentAdminApi.deleteInvoiceFile(row.invoiceFileId)
        this.patchOrderInvoice(row.orderNo, { invoiceFileId: null, invoiceFileName: null, invoiceFileUrl: null })
        if (this.selectedOrder && this.selectedOrder.orderNo === row.orderNo) {
          this.selectedOrder = { ...this.selectedOrder, invoiceFileId: null, invoiceFileName: null, invoiceFileUrl: null }
        }
        this.$message.success(this.$t('controlCenter.invoices.deleteSuccess'))
      } catch (error) {
        console.error('Failed to delete invoice file:', error)
        this.$message.error(this.$t('controlCenter.invoices.deleteError'))
      } finally {
        this.deletingOrderNo = ''
      }
    },
    handleBrandChange() {
      this.pageNumber = 0
      this.detailDialogVisible = false
      this.selectedOrder = null
      this.fetchPurchaseConfig()
      this.fetchOrders()
    },
    resetFilters() {
      this.filters = {
        keyword: '',
        status: '',
        planId: '',
        paymentMethod: ''
      }
      this.pageNumber = 0
      this.fetchOrders()
    },
    handlePageChange(page) {
      this.pageNumber = page - 1
      this.fetchOrders()
    },
    handleSizeChange(size) {
      this.pageSize = size
      this.pageNumber = 0
      this.fetchOrders()
    },
    planLabel(planId) {
      const plan = this.planOptions.find(item => item.value === planId)
      return plan ? plan.label : planId || '-'
    },
    billingCycleLabel(value) {
      const key = `controlCenter.billingCycleLabels.${value || 'one_year'}`
      const text = this.$t(key)
      return text === key ? value || '-' : text
    },
    statusLabel(status) {
      const key = `controlCenter.statusLabels.${status || 'UNKNOWN'}`
      const text = this.$t(key)
      return text === key ? status || this.$t('controlCenter.common.unknown') : text
    },
    statusTagType(status) {
      const normalized = (status || '').toUpperCase()
      if (normalized === 'SUCCESS' || normalized === 'ACTIVE') return 'success'
      if (normalized === 'PENDING') return 'warning'
      return 'danger'
    },
    formatAmount(amount, currency = 'RMB') {
      return `${currency} ${formatPrice(Number(amount || 0) / 100)}`
    },
    formatDateTime(value) {
      if (!value) return '-'
      return value.replace('T', ' ').slice(0, 19)
    }
  }
}
</script>

<style scoped>
.filter-row {
  gap: 12px;
  flex-wrap: wrap;
}

.keyword-input {
  width: 260px;
}

.filter-select {
  width: 180px;
}

.invoice-table {
  max-width: 100%;
  min-width: 0 !important;
}

.pagination-wrap {
  display: flex;
  justify-content: flex-end;
  padding: 0 24px 24px;
}

.action-buttons {
  display: flex;
  gap: 8px;
  align-items: center;
}

.invoice-upload {
  display: inline-flex;
}

.btn.btn-sm.btn-invoice-uploaded {
  background-color: rgba(39, 174, 96, 0.14);
  color: #1f8a4c;
  box-shadow: inset 0 0 0 1px rgba(39, 174, 96, 0.24);
}

.btn.btn-sm.btn-invoice-uploaded:hover {
  background-color: rgba(39, 174, 96, 0.22);
  color: #18723f;
}

.detail-layout {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.detail-grid,
.company-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.detail-card {
  margin-bottom: 0;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 6px;

  span {
    color: #64748b;
    font-size: 12px;
  }

  strong {
    color: #0f172a;
    font-size: 14px;
  }
}

.detail-span-2 {
  grid-column: span 2;
}

.invoice-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid rgba(148, 163, 184, 0.16);
}

.invoice-row:last-child {
  border-bottom: none;
}

@media screen and (max-width: 768px) {
  .keyword-input,
  .filter-select {
    width: 100%;
  }

  .pagination-wrap {
    justify-content: center;
    padding: 0 12px 18px;
  }

  .action-buttons {
    flex-direction: column;
    align-items: stretch;
  }

  .action-buttons .el-button,
  .action-buttons .invoice-upload {
    width: 100%;
    margin-left: 0;
  }

  .action-buttons .invoice-upload .el-button {
    width: 100%;
  }

  .detail-grid,
  .company-grid {
    grid-template-columns: 1fr;
  }

  .detail-span-2 {
    grid-column: span 1;
  }

  .invoice-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }
}

/* ===== 品牌化 UI 重设计 ===== */
.invoice-management-page {
  --brand-gradient: linear-gradient(135deg, rgba(6, 73, 184, 0.96) 0%, rgba(10, 127, 215, 0.92) 52%, rgba(3, 210, 197, 0.88) 100%);
  --brand-blue: #0a7fd7;
  --brand-deep: #0649b8;
  --brand-teal: #03d2c5;
  --ink: #0f172a;
  --muted: #64748b;
  --line: #e4edf7;
  --surface: #ffffff;
}

.invoice-management-page .main-header {
  position: relative;
  padding-left: 18px;
}

.invoice-management-page .main-header::before {
  content: '';
  position: absolute;
  left: 0;
  top: 6px;
  bottom: 6px;
  width: 5px;
  border-radius: 999px;
  background: var(--brand-gradient);
}

.invoice-management-page .main-header h1 {
  color: var(--ink);
  font-weight: 800;
  letter-spacing: -0.02em;
}

.invoice-management-page .subtitle {
  color: var(--muted);
}

.invoice-management-page .card {
  border-radius: 16px;
  border-color: var(--line);
  box-shadow: 0 18px 44px rgba(15, 23, 42, 0.07);
  overflow: hidden;
}

.invoice-management-page .card-header.filter-row {
  background: linear-gradient(180deg, #fbfdff 0%, #f3f8fe 100%);
  padding: 18px 20px;
  border-bottom: 1px solid var(--line);
}

.invoice-management-page ::v-deep .keyword-input .el-input__inner,
.invoice-management-page ::v-deep .filter-select .el-input__inner {
  border-radius: 10px;
  border-color: #dbe7f5;
  background-color: #fff;
}

.invoice-management-page ::v-deep .keyword-input .el-input__inner:focus,
.invoice-management-page ::v-deep .filter-select .el-input__inner:focus {
  border-color: var(--brand-blue);
  box-shadow: 0 0 0 3px rgba(10, 127, 215, 0.12);
}

.invoice-management-page .btn.btn-primary {
  background: var(--brand-gradient);
  border-radius: 10px;
  box-shadow: 0 8px 18px rgba(10, 127, 215, 0.26);
}

.invoice-management-page .btn.btn-primary:hover {
  transform: translateY(-1px);
  filter: brightness(1.03);
  box-shadow: 0 10px 22px rgba(10, 127, 215, 0.32);
}

.invoice-management-page ::v-deep .el-table th {
  background: #eef4fc !important;
  color: #0a5aa8;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border-bottom: 1px solid #dbe7f5;
  padding: 12px 0;
}

.invoice-management-page ::v-deep .el-table td {
  border-bottom: 1px solid #eef3f9;
}

.invoice-management-page ::v-deep .el-table__body tr:hover > td {
  background: #f4f9ff !important;
}

.invoice-management-page ::v-deep .el-table__row--level-1 > td {
  background: #f7faff;
}

.invoice-management-page ::v-deep .status-pill.el-tag {
  border: none;
  border-radius: 999px;
  padding: 4px 11px;
  font-weight: 700;
  line-height: 1.4;
}

.invoice-management-page ::v-deep .status-pill.el-tag--success {
  background: rgba(3, 210, 197, 0.13);
  color: #0b8a76;
}

.invoice-management-page ::v-deep .status-pill.el-tag--warning {
  background: rgba(245, 158, 11, 0.15);
  color: #b45309;
}

.invoice-management-page ::v-deep .status-pill.el-tag--danger {
  background: rgba(225, 29, 72, 0.11);
  color: #be123c;
}

.invoice-management-page ::v-deep .storage-tag.el-tag {
  background: var(--brand-gradient);
  color: #fff;
  border: none;
  border-radius: 999px;
  padding: 3px 10px;
  font-weight: 600;
  line-height: 1.4;
}

.invoice-file-cell {
  color: var(--muted);
}

.invoice-file-cell.is-uploaded {
  color: #0b8a76;
  font-weight: 600;
}

.action-buttons .btn {
  min-width: 92px;
  justify-content: center;
  padding: 8px 14px;
  font-size: 12px;
  border-radius: 9px;
  border: none;
  font-weight: 600;
  transition: all 0.2s ease;
}

.action-buttons .action-view {
  background: #fff;
  color: var(--brand-blue);
  box-shadow: inset 0 0 0 1px rgba(10, 127, 215, 0.28);
}

.action-buttons .action-view:hover {
  background: #eaf4ff;
}

.action-buttons .action-upload {
  background: var(--brand-gradient);
  color: #fff;
  box-shadow: 0 6px 14px rgba(10, 127, 215, 0.26);
}

.action-buttons .action-upload:hover {
  transform: translateY(-1px);
  filter: brightness(1.03);
}

.action-buttons .action-invoice {
  background: #ecfdf9;
  color: #0b8a76;
  box-shadow: inset 0 0 0 1px rgba(3, 210, 197, 0.34);
}

.action-buttons .action-invoice:hover {
  background: #d7f7f0;
}

.action-buttons .action-delete {
  background: #fff1f2;
  color: #be123c;
  box-shadow: inset 0 0 0 1px rgba(225, 29, 72, 0.24);
}

.action-buttons .action-delete:hover {
  background: #ffe4e6;
}

.action-buttons .btn-invoice-uploaded {
  background: rgba(3, 210, 197, 0.16) !important;
  color: #0b8a76 !important;
  box-shadow: inset 0 0 0 1px rgba(3, 210, 197, 0.4);
}

.pagination-wrap {
  border-top: 1px solid var(--line);
  background: var(--surface);
}

.detail-card {
  border-radius: 14px;
  border-color: var(--line);
}

.detail-card .card-header {
  background: #f7fafd;
}

.detail-item span {
  color: var(--muted);
  font-weight: 600;
}

.detail-item strong {
  color: var(--ink);
  font-weight: 700;
}

.invoice-row {
  border-color: #eef3f9;
}

/* Invoice workspace redesign */
.invoice-management-page {
  --invoice-ink: #18212b;
  --invoice-muted: #66717d;
  --invoice-line: #dfe5e9;
  --invoice-soft: #f4f6f7;
  --invoice-green: #176d68;
  --invoice-green-soft: #e8f4f1;
  --invoice-gold: #a86816;
  --invoice-gold-soft: #fff4df;
  --invoice-coral: #b64d3e;
  --invoice-coral-soft: #fff0ed;
  --invoice-blue: #315f87;
  color: var(--invoice-ink);
}

.invoice-hero {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  padding: 4px 0 22px;
  border-bottom: 1px solid var(--invoice-line);
}

.hero-copy {
  min-width: 0;
}

.hero-eyebrow,
.section-kicker {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: var(--invoice-green);
  font-size: 11px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0;
  text-transform: uppercase;
}

.invoice-hero h1 {
  margin: 7px 0 5px;
  color: var(--invoice-ink);
  font-size: 30px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0;
}

.invoice-hero .subtitle {
  max-width: 640px;
  margin: 0;
  color: var(--invoice-muted);
  font-size: 14px;
  line-height: 1.6;
}

.hero-actions {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  flex: 0 0 auto;
}

.brand-switcher {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 160px;
}

.brand-switcher > span {
  color: var(--invoice-muted);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0;
}

.invoice-management-page ::v-deep .brand-switcher .el-input__inner {
  height: 38px;
  border-color: var(--invoice-line);
  border-radius: 6px;
  color: var(--invoice-ink);
  font-weight: 600;
}

.btn.btn-icon-only {
  width: 38px;
  height: 38px;
  padding: 0;
  justify-content: center;
  border: 1px solid var(--invoice-line);
  border-radius: 6px;
  background: #fff;
  color: var(--invoice-muted);
  box-shadow: none;
}

.btn.btn-icon-only i {
  margin: 0;
}

.btn.btn-icon-only:hover {
  border-color: var(--invoice-green);
  color: var(--invoice-green);
  background: var(--invoice-green-soft);
}

.invoice-metrics {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin: 18px 0;
}

.metric-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 13px;
  min-height: 84px;
  padding: 15px 16px;
  overflow: hidden;
  border: 1px solid var(--invoice-line);
  border-radius: 8px;
  background: #fff;
}

.metric-card::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 3px;
  background: #87929d;
}

.metric-card--success::after { background: var(--invoice-green); }
.metric-card--warning::after { background: var(--invoice-gold); }
.metric-card--accent::after { background: var(--invoice-coral); }

.metric-icon {
  display: grid;
  width: 42px;
  height: 42px;
  place-items: center;
  flex: 0 0 42px;
  border-radius: 7px;
  background: var(--invoice-soft);
  color: #52606c;
  font-size: 17px;
}

.metric-card--success .metric-icon { background: var(--invoice-green-soft); color: var(--invoice-green); }
.metric-card--warning .metric-icon { background: var(--invoice-gold-soft); color: var(--invoice-gold); }
.metric-card--accent .metric-icon { background: var(--invoice-coral-soft); color: var(--invoice-coral); }

.metric-content {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 3px;
}

.metric-content span {
  overflow: hidden;
  color: var(--invoice-muted);
  font-size: 11px;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.metric-content strong {
  color: var(--invoice-ink);
  font-size: 24px;
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  line-height: 1;
}

.metric-suffix {
  align-self: flex-end;
  margin: 0 10px 5px -8px;
  color: var(--invoice-muted);
  font-size: 12px;
  font-weight: 700;
}

.invoice-workspace {
  overflow: hidden;
  border: 1px solid var(--invoice-line);
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(24, 33, 43, 0.05);
}

.workspace-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 17px 18px 14px;
}

.workspace-title,
.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.workspace-title h2 {
  margin: 2px 0 0;
  color: var(--invoice-ink);
  font-size: 18px;
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: 0;
}

.record-count {
  display: inline-grid;
  min-width: 31px;
  height: 24px;
  padding: 0 8px;
  place-items: center;
  border-radius: 12px;
  background: var(--invoice-soft);
  color: var(--invoice-muted);
  font-size: 12px;
  font-weight: 700;
}

.text-button {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 10px;
  border: 0;
  background: transparent;
  color: var(--invoice-muted);
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
}

.text-button:hover {
  color: var(--invoice-coral);
}

.invoice-management-page .btn.btn-primary {
  min-height: 38px;
  padding: 9px 16px;
  border-radius: 6px;
  background: var(--invoice-green);
  box-shadow: none;
  color: #fff;
  letter-spacing: 0;
}

.invoice-management-page .btn.btn-primary:hover {
  transform: none;
  background: #115c58;
  box-shadow: none;
  filter: none;
}

.filter-panel {
  position: relative;
  display: grid;
  grid-template-columns: minmax(240px, 1.6fr) repeat(3, minmax(150px, 1fr));
  gap: 10px;
  padding: 14px 18px;
  border-top: 1px solid var(--invoice-line);
  border-bottom: 1px solid var(--invoice-line);
  background: #f7f9fa;
}

.keyword-input,
.filter-select {
  width: 100%;
}

.invoice-management-page ::v-deep .filter-panel .el-input__inner {
  height: 38px;
  border-color: #d8dfe3;
  border-radius: 6px;
  background: #fff;
  color: var(--invoice-ink);
}

.invoice-management-page ::v-deep .filter-panel .el-input__inner:focus {
  border-color: var(--invoice-green);
  box-shadow: 0 0 0 2px rgba(23, 109, 104, 0.1);
}

.filter-count {
  position: absolute;
  top: -10px;
  right: 14px;
  display: grid;
  width: 20px;
  height: 20px;
  place-items: center;
  border-radius: 10px;
  background: var(--invoice-green);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
}

.filter-chips {
  display: flex;
  gap: 7px;
  flex-wrap: wrap;
  padding: 9px 18px;
  border-bottom: 1px solid var(--invoice-line);
}

.filter-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  max-width: 260px;
  padding: 4px 8px;
  overflow: hidden;
  border-radius: 4px;
  background: var(--invoice-green-soft);
  color: var(--invoice-green);
  font-size: 11px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.invoice-table-wrap {
  width: 100%;
  overflow: hidden;
}

.invoice-table {
  width: 100%;
  max-width: 100%;
}

.invoice-management-page ::v-deep .el-table::before,
.invoice-management-page ::v-deep .el-table__fixed::before,
.invoice-management-page ::v-deep .el-table__fixed-right::before {
  display: none;
}

.invoice-management-page ::v-deep .el-table th {
  height: 45px;
  padding: 0;
  border-bottom: 1px solid var(--invoice-line);
  background: #fff !important;
  color: #707b86;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
}

.invoice-management-page ::v-deep .el-table td {
  padding: 13px 0;
  border-bottom: 1px solid #edf0f2;
}

.invoice-management-page ::v-deep .el-table__body tr:hover > td,
.invoice-management-page ::v-deep .el-table__fixed-body-wrapper tr:hover > td {
  background: #f6faf9 !important;
}

.order-cell,
.invoice-status,
.invoice-status-copy {
  display: flex;
}

.order-cell,
.invoice-status-copy {
  min-width: 0;
  flex-direction: column;
}

.order-cell {
  gap: 4px;
}

.order-no {
  color: var(--invoice-ink);
  font-family: Consolas, Monaco, monospace;
  font-size: 12px;
  font-weight: 700;
}

.order-date,
.invoice-status-copy small {
  overflow: hidden;
  color: #8a949d;
  font-size: 10px;
  line-height: 1.4;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.company-name,
.plan-name,
.amount-cell {
  color: var(--invoice-ink);
  font-size: 12px;
  font-weight: 600;
}

.amount-cell {
  font-variant-numeric: tabular-nums;
}

.plan-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 8px;
  border-radius: 4px;
  background: #edf2f6;
  color: var(--invoice-blue);
  font-size: 10px;
  font-weight: 700;
}

.invoice-management-page ::v-deep .status-pill.el-tag {
  display: inline-flex;
  height: 24px;
  align-items: center;
  gap: 6px;
  padding: 0 8px;
  border: 0;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  line-height: 24px;
}

.status-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.invoice-status {
  align-items: center;
  gap: 9px;
  min-width: 0;
}

.invoice-status-icon {
  display: grid;
  width: 28px;
  height: 28px;
  place-items: center;
  flex: 0 0 28px;
  border-radius: 5px;
  background: var(--invoice-gold-soft);
  color: var(--invoice-gold);
  font-size: 11px;
}

.invoice-status.is-uploaded .invoice-status-icon {
  background: var(--invoice-green-soft);
  color: var(--invoice-green);
}

.invoice-status-copy {
  gap: 2px;
}

.invoice-status-copy strong {
  color: var(--invoice-gold);
  font-size: 11px;
  font-weight: 700;
}

.invoice-status.is-uploaded .invoice-status-copy strong {
  color: var(--invoice-green);
}

.action-buttons {
  display: flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
}

.invoice-upload,
.invoice-upload ::v-deep .el-upload {
  display: inline-flex;
}

.action-buttons .btn {
  min-width: 34px;
  height: 32px;
  justify-content: center;
  gap: 6px;
  padding: 0 10px;
  border: 1px solid var(--invoice-line);
  border-radius: 5px;
  background: #fff;
  box-shadow: none;
  color: var(--invoice-muted);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0;
}

.action-buttons .btn i {
  margin: 0;
  font-size: 11px;
}

.action-buttons .action-view:hover { border-color: var(--invoice-blue); background: #edf4fa; color: var(--invoice-blue); }
.action-buttons .action-upload { border-color: var(--invoice-green); background: var(--invoice-green); color: #fff; }
.action-buttons .action-upload:hover { transform: none; background: #115c58; filter: none; }
.action-buttons .action-invoice { border-color: #bfcdd8; background: #f5f8fa; color: var(--invoice-blue); }
.action-buttons .action-invoice:hover { background: #e9f1f7; }
.action-buttons .action-delete { border-color: #efcfc9; background: var(--invoice-coral-soft); color: var(--invoice-coral); }
.action-buttons .action-delete:hover { background: #ffe4df; }
.action-buttons .btn-invoice-uploaded { border-color: #b8d8d3 !important; background: var(--invoice-green-soft) !important; color: var(--invoice-green) !important; }
.action-buttons .btn:disabled { cursor: not-allowed; opacity: 0.5; }

.table-empty {
  display: flex;
  min-height: 220px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 7px;
  color: var(--invoice-muted);
}

.table-empty i {
  margin-bottom: 5px;
  color: #b9c1c7;
  font-size: 28px;
}

.table-empty strong { color: var(--invoice-ink); font-size: 13px; }
.table-empty span { max-width: 340px; font-size: 11px; }

.workspace-footer {
  display: flex;
  min-height: 64px;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 10px 18px;
  border-top: 1px solid var(--invoice-line);
  background: #fafbfb;
}

.footer-hint {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: var(--invoice-muted);
  font-size: 10px;
}

.footer-hint i { color: var(--invoice-green); }

.invoice-management-page ::v-deep .el-pagination button,
.invoice-management-page ::v-deep .el-pager li {
  background: transparent;
}

.invoice-management-page ::v-deep .el-pager li.active {
  color: var(--invoice-green);
}

.invoice-management-page ::v-deep .invoice-detail-dialog .el-dialog {
  max-width: calc(100vw - 32px);
  overflow: hidden;
  border-radius: 8px;
  box-shadow: 0 24px 60px rgba(24, 33, 43, 0.2);
}

.invoice-management-page ::v-deep .invoice-detail-dialog .el-dialog__header {
  padding: 18px 22px;
  border-bottom: 1px solid var(--invoice-line);
  background: #fff;
}

.invoice-management-page ::v-deep .invoice-detail-dialog .el-dialog__body {
  max-height: calc(100vh - 190px);
  padding: 18px 22px;
  overflow-y: auto;
  background: #f5f7f8;
}

.invoice-management-page ::v-deep .invoice-detail-dialog .el-dialog__footer {
  padding: 12px 22px;
  border-top: 1px solid var(--invoice-line);
  background: #fff;
}

.dialog-title {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--invoice-ink);
  font-size: 17px;
  font-weight: 700;
}

.dialog-order-no {
  padding: 4px 7px;
  border-radius: 4px;
  background: var(--invoice-soft);
  color: var(--invoice-muted);
  font-family: Consolas, Monaco, monospace;
  font-size: 10px;
  font-weight: 600;
}

.detail-layout {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.detail-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 17px 18px;
  border-radius: 7px;
  background: var(--invoice-ink);
  color: #fff;
}

.summary-main,
.summary-side {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 5px;
}

.summary-main strong,
.summary-side strong {
  overflow-wrap: anywhere;
  font-size: 18px;
  line-height: 1.25;
}

.summary-side {
  align-items: flex-end;
  text-align: right;
}

.summary-label,
.summary-meta {
  color: #aeb7bf;
  font-size: 10px;
}

.detail-summary ::v-deep .status-pill.el-tag {
  background: rgba(255, 255, 255, 0.12);
  color: #e8f4f1;
}

.detail-grid,
.company-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1px;
  overflow: hidden;
  border: 1px solid var(--invoice-line);
  border-radius: 7px;
  background: var(--invoice-line);
}

.detail-grid--compact .detail-item,
.company-grid .detail-item {
  min-height: 64px;
  padding: 12px 14px;
  background: #fff;
}

.detail-item {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 5px;
}

.detail-item span {
  color: var(--invoice-muted);
  font-size: 10px;
  font-weight: 600;
}

.detail-item strong {
  overflow-wrap: anywhere;
  color: var(--invoice-ink);
  font-size: 12px;
  font-weight: 700;
  line-height: 1.45;
}

.detail-span-2 { grid-column: span 2; }

.file-value {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: var(--invoice-green) !important;
}

.detail-card {
  margin: 0;
  overflow: hidden;
  border: 1px solid var(--invoice-line);
  border-radius: 7px;
  background: #fff;
}

.detail-card-header {
  display: flex;
  min-height: 58px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 15px;
  border-bottom: 1px solid var(--invoice-line);
  background: #fafbfb;
}

.detail-card-header h3 {
  margin: 2px 0 0;
  color: var(--invoice-ink);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0;
}

.detail-card-body {
  padding: 12px 15px;
}

.detail-card .company-grid {
  border: 0;
  border-radius: 0;
  background: var(--invoice-line);
}

.snapshot-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 8px;
  border-radius: 4px;
  background: var(--invoice-green-soft);
  color: var(--invoice-green);
  font-size: 10px;
  font-weight: 700;
}

.invoice-row {
  display: flex;
  min-height: 38px;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 8px 0;
  border-bottom: 1px solid #edf0f2;
  color: var(--invoice-muted);
  font-size: 12px;
}

.invoice-row:last-child { border-bottom: 0; }
.invoice-row strong { color: var(--invoice-ink); font-variant-numeric: tabular-nums; }

.invoice-info-empty {
  display: flex;
  min-height: 74px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: var(--invoice-muted);
  font-size: 12px;
}

@media screen and (max-width: 1100px) {
  .invoice-metrics { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .filter-panel { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .workspace-footer { align-items: flex-start; flex-direction: column; }
  .workspace-footer ::v-deep .el-pagination { width: 100%; overflow-x: auto; }
}

@media screen and (max-width: 768px) {
  .invoice-hero { align-items: stretch; flex-direction: column; padding-bottom: 16px; }
  .invoice-hero h1 { font-size: 24px; }
  .hero-actions { align-items: flex-end; }
  .brand-switcher { width: min(100%, 220px); flex: 1 1 auto; }
  .invoice-metrics { grid-template-columns: 1fr; gap: 8px; }
  .metric-card { min-height: 72px; }
  .workspace-toolbar { align-items: flex-start; flex-direction: column; }
  .toolbar-actions { width: 100%; justify-content: flex-end; }
  .filter-panel { grid-template-columns: 1fr; }
  .action-buttons { align-items: stretch; flex-direction: column; }
  .action-buttons .btn,
  .invoice-upload,
  .invoice-upload ::v-deep .el-upload { width: 100%; }
  .detail-summary { align-items: flex-start; flex-direction: column; }
  .summary-side { width: 100%; align-items: flex-start; text-align: left; }
  .detail-grid,
  .company-grid { grid-template-columns: 1fr; }
  .detail-span-2 { grid-column: span 1; }
  .workspace-footer { padding: 12px; }
  .footer-hint { display: none; }
}
</style>
